import {alpha, styled} from "@mui/material/styles";
import {useCallback, useEffect, useRef, useState} from "react";

const trackWidthPx = 10;
const minThumbHeightPx = 28;
// Below the fixed 48px header, with a small bottom inset.
const trackTopPx = 56;
const trackBottomInsetPx = 16;
// Shortest track worth painting: below this the geometry is degenerate.
const minTrackHeightPx = 2 * minThumbHeightPx;

/**
 * Zero-height in-flow element used only to measure the results column's
 * position (both the horizontal gutter and the listing's top edge); the
 * track itself is fixed to the viewport.
 */
const Anchor = styled("div")({
    height: 0,
});

/**
 * Viewport-fixed container for the track. The track must never move with
 * scrolling: dragging it drives the page scroll, so a scroll-coupled track
 * would feed its own motion back into the pointer-to-track mapping.
 */
const FixedRoot = styled("div", {shouldForwardProp: (prop) => prop !== "leftPx" && prop !== "topPx"})<{
    leftPx: number;
    topPx: number;
}>(({leftPx, topPx}) => ({
    position: "fixed",
    top: topPx,
    left: leftPx,
    width: trackWidthPx,
    height: `calc(100vh - ${topPx + trackBottomInsetPx}px)`,
    zIndex: 10,
}));

const Track = styled("div")(({theme}) => ({
    position: "absolute",
    inset: 0,
    borderRadius: trackWidthPx / 2,
    backgroundColor: alpha(theme.palette.text.primary, 0.06),
    cursor: "pointer",
    touchAction: "none",
}));

const Thumb = styled("div", {shouldForwardProp: (prop) => prop !== "topPx" && prop !== "heightPx"})<{
    topPx: number;
    heightPx: number;
}>(({theme, topPx, heightPx}) => ({
    position: "absolute",
    left: 0,
    width: "100%",
    top: topPx,
    height: heightPx,
    borderRadius: trackWidthPx / 2,
    backgroundColor: alpha(theme.palette.text.primary, 0.35),
    "&:hover": {
        backgroundColor: alpha(theme.palette.text.primary, 0.5),
    },
}));

/**
 * Hides the native window scrollbar while enabled. Wheel, touch, keyboard,
 * and programmatic scrolling are unaffected — only the scrollbar visuals are
 * suppressed, with the custom track taking its place. Applied from the page
 * (not the track) so the suppression starts as soon as the flag resolves and
 * stays put across loading/listing transitions, instead of the viewport
 * width jumping whenever the track mounts or unmounts.
 */
export function useHideNativeScrollbar(enabled: boolean): void {
    useEffect(() => {
        if (!enabled) return undefined;
        const style = document.createElement("style");
        style.textContent = "html{scrollbar-width:none;} html::-webkit-scrollbar{display:none;}";
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, [enabled]);
}

/**
 * Custom overlay scrollbar for the virtualized search results listing: a
 * viewport-fixed track pinned beside the results column whose thumb mirrors
 * the window's scroll position (linear scale, no tickmarks or labels).
 * Dragging the thumb preserves the grab point; clicking the track centers
 * the thumb on the pointer. Native scrollbar suppression is owned by the
 * page via `useHideNativeScrollbar`.
 */
export default function SearchListingScrollbar() {
    const trackRef = useRef<HTMLDivElement | null>(null);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [trackLeftPx, setTrackLeftPx] = useState<number>();
    const [trackTopOffsetPx, setTrackTopOffsetPx] = useState(trackTopPx);
    const [trackHeightPx, setTrackHeightPx] = useState(0);
    const [scrollFraction, setScrollFraction] = useState(0);
    const [viewportFraction, setViewportFraction] = useState(1);
    const [isScrollable, setIsScrollable] = useState(false);
    // Pointer-to-thumb offset (in track pixels) captured at grab time, so
    // dragging preserves the grab point instead of snapping the thumb's top
    // to the pointer. Undefined while not dragging.
    const dragOffsetPxRef = useRef<number>();

    const remeasure = useCallback(() => {
        const scrollHeight = document.documentElement.scrollHeight;
        const maxScrollY = Math.max(0, scrollHeight - window.innerHeight);
        setIsScrollable(maxScrollY > 1);
        setScrollFraction(maxScrollY > 0 ? Math.min(1, Math.max(0, window.scrollY / maxScrollY)) : 0);
        setViewportFraction(scrollHeight > 0 ? Math.min(1, window.innerHeight / scrollHeight) : 1);
        const anchor = anchorRef.current;
        if (anchor) {
            const rect = anchor.getBoundingClientRect();
            // The track hangs into the right gutter beside the results
            // column, clamped inside the viewport so narrow layouts pin it
            // to the right edge instead of dropping the bar.
            const left = Math.min(rect.right + 18, window.innerWidth) - trackWidthPx;
            setTrackLeftPx((previous) => (previous === left ? previous : left));
            // The track starts at the listing's top edge (not the header's
            // bottom), so the thumb sits beside the results rather than
            // floating above them; once the listing scrolls under the
            // header the track occupies its full extent.
            const top = Math.min(
                Math.max(trackTopPx, Math.round(rect.top)),
                window.innerHeight - trackBottomInsetPx - minTrackHeightPx,
            );
            setTrackTopOffsetPx((previous) => (previous === top ? previous : top));
            const height = window.innerHeight - top - trackBottomInsetPx;
            setTrackHeightPx((previous) => (previous === height ? previous : height));
        }
    }, []);

    useEffect(() => {
        let rafId: number | undefined;
        const scheduleRemeasure = () => {
            if (rafId !== undefined) return;
            rafId = requestAnimationFrame(() => {
                rafId = undefined;
                remeasure();
            });
        };
        remeasure();
        window.addEventListener("scroll", scheduleRemeasure, {passive: true});
        window.addEventListener("resize", scheduleRemeasure);
        // Document height changes (rows loading and remeasuring) move the
        // thumb and decide whether the page overflows at all.
        const resizeObserver = new ResizeObserver(scheduleRemeasure);
        resizeObserver.observe(document.body);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("scroll", scheduleRemeasure);
            window.removeEventListener("resize", scheduleRemeasure);
            if (rafId !== undefined) cancelAnimationFrame(rafId);
        };
    }, [remeasure]);

    // Painted thumb geometry, in track pixels. The pointer handlers derive
    // the same values from the live track rect, so the hit test and the
    // painted thumb can never disagree (a mismatch reads as the thumb
    // "jumping" to the pointer on grab).
    const thumbPx = Math.max(minThumbHeightPx, viewportFraction * trackHeightPx);
    const thumbTopPx = scrollFraction * Math.max(0, trackHeightPx - thumbPx);

    const scrollToTrackPointer = useCallback((clientY: number, grabOffsetPx: number) => {
        const track = trackRef.current;
        if (!track) return;
        const rect = track.getBoundingClientRect();
        const thumb = Math.max(
            minThumbHeightPx,
            rect.height * (window.innerHeight / document.documentElement.scrollHeight),
        );
        const travelPx = Math.max(1, rect.height - thumb);
        const fraction = Math.min(1, Math.max(0, (clientY - rect.top - grabOffsetPx) / travelPx));
        const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        window.scrollTo({top: fraction * maxScrollY});
    }, []);

    const onPointerDown = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            const track = trackRef.current;
            if (!track) return;
            event.preventDefault();
            track.setPointerCapture(event.pointerId);
            const rect = track.getBoundingClientRect();
            const thumb = Math.max(
                minThumbHeightPx,
                rect.height * (window.innerHeight / document.documentElement.scrollHeight),
            );
            const travelPx = Math.max(1, rect.height - thumb);
            const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
            const grabbedThumbTopPx = (maxScrollY > 0 ? window.scrollY / maxScrollY : 0) * travelPx;
            const pointerPx = event.clientY - rect.top;
            // Grabbing the thumb keeps the grab point; grabbing the track
            // centers the thumb on the pointer, then drags from there.
            const grabOffsetPx =
                pointerPx >= grabbedThumbTopPx && pointerPx <= grabbedThumbTopPx + thumb
                    ? pointerPx - grabbedThumbTopPx
                    : thumb / 2;
            dragOffsetPxRef.current = grabOffsetPx;
            scrollToTrackPointer(event.clientY, grabOffsetPx);
        },
        [scrollToTrackPointer],
    );

    const onPointerMove = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
            const grabOffsetPx = dragOffsetPxRef.current;
            if (grabOffsetPx === undefined) return;
            scrollToTrackPointer(event.clientY, grabOffsetPx);
        },
        [scrollToTrackPointer],
    );

    const endDrag = useCallback(() => {
        dragOffsetPxRef.current = undefined;
    }, []);

    return (
        <>
            <Anchor ref={anchorRef} aria-hidden={true} />
            {trackLeftPx !== undefined && isScrollable && (
                <FixedRoot leftPx={trackLeftPx} topPx={trackTopOffsetPx}>
                    <Track
                        ref={trackRef}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        data-testid="search-listing-scrollbar"
                    >
                        <Thumb topPx={thumbTopPx} heightPx={thumbPx} />
                    </Track>
                </FixedRoot>
            )}
        </>
    );
}
